import { WebComponent } from '@cian/mf-registry/browser';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';

import { IApplicationContext } from '../../shared/types/applicationContext';
import { IReduxStore } from '../../shared/types/redux';
import { Application } from '../../shared/containers/App';
import { V3_RECOMMENDATIONS_RUNTIME_NAME } from '../../shared/constants/runtimeNames';

export function createCustomElementConstructor(
  context: IApplicationContext,
  store: IReduxStore,
): CustomElementConstructor {
  class Recommendations extends WebComponent {
    private applicationContext: IApplicationContext;
    private disconnectTimerId: ReturnType<typeof setTimeout> | null = null;
    private reactRoot: ReactDOM.Root | null = null;

    protected microfrontendName = context.config.getStrict<string>('projectName');
    protected imageVersion = '';
    protected runtimeName = V3_RECOMMENDATIONS_RUNTIME_NAME;

    public api = {};

    public async connectedCallback(): Promise<void> {
      if (this.disconnectTimerId) {
        clearTimeout(this.disconnectTimerId);
        this.disconnectTimerId = null;
      }

      const instanceId = this.getAttribute('instance') || undefined;
      this.applicationContext = { ...context, config: context.config.attach({ instanceId }) };
      this.imageVersion = this.applicationContext.config.getStrict<string>('version.commit');
      await super.connectedCallback();
    }

    public disconnectedCallback() {
      if (this.disconnectTimerId) {
        clearTimeout(this.disconnectTimerId);
      }

      this.disconnectTimerId = setTimeout(() => {
        if (this.isConnected) {
          this.disconnectTimerId = null;

          return;
        }

        this.reactRoot?.unmount();
        this.reactRoot = null;
        super.disconnectedCallback();
        this.disconnectTimerId = null;
      }, 0);
    }

    public async render() {
      const reactElement = <Application {...this.props} context={this.applicationContext} reduxStore={store} />;

      if (!this.reactRoot) {
        if (this.shouldHydrate) {
          this.reactRoot = ReactDOM.hydrateRoot(this, reactElement);

          return;
        }

        this.reactRoot = ReactDOM.createRoot(this);
        this.reactRoot.render(reactElement);

        return;
      }

      this.reactRoot.render(reactElement);
    }
  }

  return Recommendations;
}
